import React, { useState, useEffect, useMemo, useRef } from "react";
import { Input } from "antd";
import { useSearchParams } from "react-router-dom";
import Pagination from "../../../components/global/pagination/Pagination.jsx";
import columns from "../../../columns/ibCommission/withdrawCommission/WithdrawCommission.js";
import TableWithSkeleton from "../../../components/global/table/TableWithSkeleton.jsx";
import { GetWithdrawCommissionApi } from "../../../api/request/ibCommission.js";
import moment from "moment";
import ExportHandler from "../../../components/global/export/ExportHandler.jsx";
import UserAvatar from "../../../components/global/avtar/UserAvatar.jsx";
import useModulePermissions from "../../../hooks/useModulePermissions.jsx";

export default function WithdrawCommission() {
  const commissionPermissions = useModulePermissions(
    "commission",
    "comm_withdraw",
  );
  const canAcceptReject = !!commissionPermissions.accept_reject;
  const canExport = !!commissionPermissions.export;

  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecord, setTotalRecord] = useState(0);
  const [search, setSearch] = useState("");
  const [searchText, setSearchText] = useState("");
  const [filter, setFilter] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const fetchRequestIdRef = useRef(0);

  const tableColumns = useMemo(
    () => [
      {
        title: "No.",
        dataIndex: "no",
        width: 50,
        render: (no) => <span className="ps-2">{no}</span>,
      },
      ...columns,
    ],
    [],
  );

  const mapCommissionRows = (items, pageNumber, pageSizeValue) =>
    (items ?? []).map((item, index) => ({
      ...item,
      key: item?.requestId ?? item?.id ?? `row-${index}`,
      no: (pageNumber - 1) * pageSizeValue + index + 1,
      name: (
        <UserAvatar
          data={{
            name: `${item?.firstName} ${item?.lastName}`,
            email: item?.primaryEmail,
          }}
        />
      ),
      transactionDate: moment(item?.transactionDate).format(
        "DD-MM-YYYY, hh:mm A",
      ),
      commissionAmount: `$ ${item?.commissionAmount}`,
      status:
        item?.status == "Success" ? (
          <p className="uppercase text-[#2d9b63] text-sm font-semibold">
            {item?.status}
          </p>
        ) : (
          <p className="uppercase text-[#F82223] text-sm font-semibold">
            {item?.status}
          </p>
        ),
    }));

  useEffect(() => {
    const requestId = ++fetchRequestIdRef.current;

    const fetchCommission = async () => {
      setIsLoading(true);
      setData([]);

      try {
        const params = {
          PageNumber: page,
          PageSize: pageSize,
          SearchText: searchText,
          FilterText: "Debit",
        };
        const { data: response } = await GetWithdrawCommissionApi(
          new URLSearchParams(params).toString(),
        );

        if (requestId !== fetchRequestIdRef.current) return;

        setData(mapCommissionRows(response?.data, page, pageSize));
        setTotalRecord(response?.totalRecords ?? 0);
      } catch (error) {
        if (requestId === fetchRequestIdRef.current) {
          setData([]);
          setTotalRecord(0);
        }
      } finally {
        if (requestId === fetchRequestIdRef.current) {
          setIsLoading(false);
        }
      }
    };

    fetchCommission();
  }, [page, pageSize, filter, searchText]);

  const handleSearch = () => {
    setData([]);
    setSearchText(search);
    if (page !== 1) {
      setPage(1);
    }
  };

  const handleClearSearch = () => {
    setData([]);
    setSearch("");
    setSearchText("");
    if (page !== 1) {
      setPage(1);
    }
  };

  const exportHandler = async () => {
    try {
      let params = {
        ApplyPagination: false,
        SearchText: searchText,
        FilterText: "Debit",
      };
      const { data } = await GetWithdrawCommissionApi(
        new URLSearchParams(params).toString(),
      );
      if (data?.success) {
        return {
          data: data?.data,
          removableColumns: ["id"],
          fileName: "Withdraw Commission List",
        };
      } else {
        return { data: [], removableColumns: [] };
      }
    } catch (error) {
      return { data: [], removableColumns: [] };
    }
  };

  useEffect(() => {
    const trimmedCsv = searchParams?.get("filter")?.trim();
    if (trimmedCsv) {
      const newArray = trimmedCsv.split(",").map((item) => item.trim());
      setFilter(newArray);
    }
  }, []);

  return (
    <div>
      <div className="flex justify-end flex-wrap gap-5">
        <div className="flex flex-wrap items-center gap-3">
          <Input
            size="large"
            style={{ width: 220 }}
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onPressEnter={handleSearch}
            prefix={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="w-5 h-5"
              >
                <path
                  fillRule="evenodd"
                  d="M10.5 3.75a6.75 6.75 0 1 0 0 13.5 6.75 6.75 0 0 0 0-13.5ZM2.25 10.5a8.25 8.25 0 1 1 14.59 5.28l4.69 4.69a.75.75 0 1 1-1.06 1.06l-4.69-4.69A8.25 8.25 0 0 1 2.25 10.5Z"
                  clipRule="evenodd"
                />
              </svg>
            }
            suffix={
              search && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  onClick={handleClearSearch}
                  viewBox="0 0 24 24"
                  fill="#bebebe"
                  className="size-5 cursor-pointer duration-300 hover:fill-[#969595]"
                >
                  <path
                    fillRule="evenodd"
                    d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25Zm-1.72 6.97a.75.75 0 1 0-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 1 0 1.06 1.06L12 13.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L13.06 12l1.72-1.72a.75.75 0 1 0-1.06-1.06L12 10.94l-1.72-1.72Z"
                    clipRule="evenodd"
                  />
                </svg>
              )
            }
          />
          {canExport && <ExportHandler exportHandler={exportHandler} />}
        </div>
      </div>

      <div className="my-6">
        <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
          <TableWithSkeleton
            key={`${page}-${pageSize}-${searchText}`}
            columns={tableColumns}
            data={data}
            loading={isLoading}
            hiddenColumns={!canAcceptReject ? ["Actions"] : []}
            showIndexColumn={false}
          />
        </div>
        <Pagination
          total={totalRecord}
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
      </div>
    </div>
  );
}
